import { useState, useRef } from "react";
import api, { errMsg, imageSrc } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Eyebrow } from "@/components/Eyebrow";
import { Upload, Loader2, AlertTriangle, ImageOff } from "lucide-react";

const PLACEMENTS = [
  { id: "top-left", label: "TL" },
  { id: "top-right", label: "TR" },
  { id: "bottom-left", label: "BL" },
  { id: "bottom-right", label: "BR" },
  { id: "none", label: "Off" },
];

export function LogoPanel({ brandId, logo, onChange }) {
  const [current, setCurrent] = useState(logo || null);
  const [uploading, setUploading] = useState(false);
  const [placement, setPlacement] = useState((logo && logo.placement) || "bottom-right");
  const fileRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("brandId", brandId);
      fd.append("file", file);
      const { data } = await api.post("/brand/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCurrent({ ...data.logo, _t: Date.now() });
      setPlacement(data.logo.placement || "bottom-right");
      onChange?.(data.logo);
      toast.success("Logo updated", { description: "It'll be composited onto new renders." });
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const changePlacement = async (p) => {
    setPlacement(p);
    try {
      await api.post("/brand/logo-placement", { brandId, placement: p });
      setCurrent((c) => (c ? { ...c, placement: p } : c));
    } catch (e) {
      toast.error(errMsg(e, "Could not update placement"));
    }
  };

  const logoSrc = current?.url ? imageSrc(current.url) + (current._t ? `?t=${current._t}` : "") : null;

  return (
    <div className="panel-card" style={{ padding: 22 }} data-testid="logo-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Eyebrow testId="eyebrow-logo">Logo</Eyebrow>
          <p className="font-body" style={{ fontSize: 12.5, color: "var(--tsecondary)", margin: "6px 0 0" }}>
            Your real logo is composited onto renders programmatically — never redrawn by AI.
          </p>
        </div>
        <button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-secondary font-mono" data-testid="logo-upload-btn" style={{ fontSize: 11, padding: "7px 11px", flexShrink: 0 }}>
          {uploading ? <Loader2 size={12} className="spin" /> : <Upload size={12} />} {current ? "Replace" : "Upload"}
        </button>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" data-testid="logo-file-input" style={{ display: "none" }} onChange={(e) => upload(e.target.files?.[0])} />
      </div>

      <div style={{ marginTop: 16, display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
        {logoSrc ? (
          <div data-testid="logo-preview" style={{ width: 96, height: 96, borderRadius: 10, border: "1px solid var(--cardborder)", background: "repeating-conic-gradient(var(--input) 0% 25%, transparent 0% 50%) 50% / 16px 16px", display: "grid", placeItems: "center", overflow: "hidden" }}>
            <img src={logoSrc} alt="Brand logo" style={{ maxWidth: "84%", maxHeight: "84%", objectFit: "contain" }} onError={(e) => { e.currentTarget.style.opacity = "0.2"; }} />
          </div>
        ) : (
          <div data-testid="logo-empty" style={{ width: 96, height: 96, borderRadius: 10, border: "1px dashed var(--cardborder)", display: "grid", placeItems: "center", color: "var(--tmuted)" }}>
            <ImageOff size={22} />
          </div>
        )}

        <div style={{ flex: 1, minWidth: 200 }}>
          {current?.lowRes && (
            <div data-testid="logo-lowres" className="flex items-center gap-1.5 font-mono" style={{ fontSize: 11, color: "var(--orange)", marginBottom: 10 }}>
              <AlertTriangle size={13} /> Low-resolution logo — upload a crisp PNG for best results.
            </div>
          )}
          <div className="font-mono" style={{ fontSize: 10.5, color: "var(--tmuted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 7 }}>Placement</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {PLACEMENTS.map((p) => (
              <button
                key={p.id}
                data-testid={`logo-placement-${p.id}`}
                onClick={() => changePlacement(p.id)}
                disabled={!current}
                className="font-mono"
                style={{
                  fontSize: 11, padding: "6px 12px", borderRadius: 8, cursor: current ? "pointer" : "not-allowed",
                  border: `1px solid ${placement === p.id ? "var(--orange)" : "var(--cardborder)"}`,
                  background: placement === p.id ? "var(--orange)" : "transparent",
                  color: placement === p.id ? "#fff" : "var(--tsecondary)",
                }}
              >{p.label}</button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
